/**
 * mCTSIB Score Calculations
 *
 * This module implements the CTSIB scoring calculations for the mobile Proactive Strides
 * application, adapted from the desktop VirtuBalance 3D sensor application.
 *
 * Key differences from desktop:
 * - 2-axis only (X = sideways, Y = forward/backward) - no vertical
 * - Units: feet (consistent with accuracy-improvements branch output)
 * - Time limit: 10 seconds per sub-assessment (vs 30 seconds on desktop)
 */

// Configuration constants (in feet, converted from desktop's meter values)
const MCTSIB_CONFIG = {
  // Weight factors (in feet)
  // Original desktop values: 0.06m, 0.1m, 0.5m
  // Converted: value_meters * 3.281 = value_feet
  VELOCITY_WEIGHT: 0.197, // 0.06m * 3.281 = 0.197 feet/sec is "average" sway velocity
  DISPLACEMENT_WEIGHT: 0.328, // 0.1m * 3.281 = 0.328 feet is "average" mean displacement
  RANGE_WEIGHT: 1.64, // 0.5m * 3.281 = 1.64 feet is "average" total range

  // Score mapping constants (dimensionless - same as desktop)
  BEST_RAW: 0.22, // Composite score for excellent balance
  WORST_RAW: 2.67, // Composite score for poor balance (8.0 / 3.0)

  // Time settings
  TIME_LIMIT_SEC: 30, // Time limit per sub-assessment (matching 3D for comparison)

  // Feature flags for debugging/calibration
  LOG_CALIBRATION_DATA: false, // Set true to log composite scores for tuning
};

/**
 * Calculates metrics for a single sub-assessment from sway data.
 *
 * @param {number[]} sway_x - Array of X (sideways) sway values in feet
 * @param {number[]} sway_y - Array of Y (forward/backward) sway values in feet
 * @param {number} elapsedMs - Duration of the assessment in milliseconds
 * @returns {Object|null} - Metrics object or null if invalid data
 */
export function calculateSubAssessmentMetrics(sway_x, sway_y, elapsedMs) {
  // Edge case: No data or invalid elapsed time
  if (!sway_x || !sway_y || sway_x.length === 0 || sway_y.length === 0) {
    console.warn("calculateSubAssessmentMetrics: No sway data provided");
    return null;
  }

  if (elapsedMs <= 0) {
    console.warn(
      "calculateSubAssessmentMetrics: Invalid elapsed time:",
      elapsedMs,
    );
    return null;
  }

  const elapsedSec = elapsedMs / 1000;

  // Edge case: Very short test
  if (elapsedSec < 1) {
    console.warn(
      "calculateSubAssessmentMetrics: Test too short:",
      elapsedSec,
      "seconds",
    );
    // Still calculate but will have heavy time penalty
  }

  // 1. Cumulative Sway (feet) - Total 2D path distance traveled
  let cumulativeSway = 0;
  for (let i = 1; i < sway_x.length; i++) {
    const dx = sway_x[i] - sway_x[i - 1];
    const dy = sway_y[i] - sway_y[i - 1];
    cumulativeSway += Math.sqrt(dx * dx + dy * dy);
  }

  // 2. Mean Distance From Start (feet) - Average distance from starting position
  // First frame should be (0, 0) or close to it since it's relative to reference
  const startX = sway_x[0];
  const startY = sway_y[0];
  let sumDistFromStart = 0;
  for (let i = 0; i < sway_x.length; i++) {
    const dx = sway_x[i] - startX;
    const dy = sway_y[i] - startY;
    sumDistFromStart += Math.sqrt(dx * dx + dy * dy);
  }
  const meanDistFromStart = sumDistFromStart / sway_x.length;

  // 3. Range X (feet) - Total envelope of left-right movement
  const rangeX = Math.max(...sway_x) - Math.min(...sway_x);

  // 4. Range Y (feet) - Total envelope of forward-backward movement
  const rangeY = Math.max(...sway_y) - Math.min(...sway_y);

  // 5. Total Range (feet) - Sum of both axes (2-axis version)
  const totalRange = rangeX + rangeY;

  // 6. Sway Velocity (feet/second)
  const swayVelocity = cumulativeSway / elapsedSec;

  // Edge case: Check for negative values (should never happen, indicates bug)
  if (cumulativeSway < 0 || meanDistFromStart < 0 || rangeX < 0 || rangeY < 0) {
    console.error(
      "calculateSubAssessmentMetrics: Negative metric values detected - this is a bug",
    );
    return null;
  }

  // Edge case: No sway detected (patient not tracked or perfectly still)
  if (cumulativeSway === 0 && meanDistFromStart === 0) {
    console.warn(
      "calculateSubAssessmentMetrics: No sway detected - patient may not have been tracked",
    );
    // Return metrics anyway - will get low score but not null
  }

  const metrics = {
    cumulativeSway,
    meanDistFromStart,
    rangeX,
    rangeY,
    totalRange,
    elapsedSec,
    swayVelocity,
    frameCount: sway_x.length,
    fps: sway_x.length / elapsedSec,
  };

  // Log FPS for monitoring (frame rate affects cumulative sway accuracy)
  if (metrics.fps < 10) {
    console.warn(
      "calculateSubAssessmentMetrics: Low frame rate may affect accuracy:",
      metrics.fps.toFixed(1),
      "FPS",
    );
  }
  if (metrics.fps > 40) {
    console.warn(
      "calculateSubAssessmentMetrics: High frame rate may inflate cumulative sway:",
      metrics.fps.toFixed(1),
      "FPS",
    );
  }

  return metrics;
}

/**
 * Validates metrics and returns warnings for unrealistic values.
 *
 * @param {Object} metrics - Metrics object from calculateSubAssessmentMetrics
 * @param {string} condition - Name of the sub-assessment (for logging)
 * @returns {Object} - { isValid: boolean, warnings: string[] }
 */
export function validateMetrics(metrics, condition) {
  const warnings = [];

  if (!metrics) {
    return { isValid: false, warnings: ["No metrics data"] };
  }

  // Sanity checks based on typical human balance capabilities

  // Cumulative sway > 8.33 feet (100 inches) in 10 seconds is very excessive
  if (metrics.cumulativeSway > 8.33) {
    warnings.push(
      `Cumulative sway unusually high: ${metrics.cumulativeSway.toFixed(2)} feet`,
    );
  }

  // Range > 2 feet suggests tracking issue or fall
  if (metrics.rangeX > 2) {
    warnings.push(`Range X unusually large: ${metrics.rangeX.toFixed(2)} feet`);
  }
  if (metrics.rangeY > 2) {
    warnings.push(`Range Y unusually large: ${metrics.rangeY.toFixed(2)} feet`);
  }

  // Mean displacement > 1 foot is very large
  if (metrics.meanDistFromStart > 1) {
    warnings.push(
      `Mean displacement very large: ${metrics.meanDistFromStart.toFixed(2)} feet`,
    );
  }

  // Almost no sway detected (< 0.0083 feet = 0.1 inches) suggests tracking lost or patient not moving
  if (metrics.cumulativeSway < 0.0083) {
    warnings.push("Very little sway detected - verify patient was tracked");
  }

  // Log warnings
  if (warnings.length > 0) {
    console.warn(`Validation warnings for ${condition}:`, warnings);
  }

  return {
    isValid: warnings.length === 0,
    warnings,
  };
}

/**
 * Calculates the phase score (0-100) for a single sub-assessment.
 *
 * Formula:
 * 1. compositeScore = (swayVelocity / 2.36) + (meanDist / 3.94) + (totalRange / 19.7)
 * 2. phaseScore = 100 - (100 * (composite - 0.22) / (2.67 - 0.22))
 * 3. phaseScore = phaseScore * (elapsedSec / TIME_LIMIT_SEC)  // Time penalty
 * 4. phaseScore = clamp(phaseScore, 0, 100)
 *
 * @param {Object} metrics - Metrics object from calculateSubAssessmentMetrics
 * @returns {number} - Phase score from 0-100
 */
export function calculatePhaseScore(metrics) {
  // Edge case: No metrics
  if (!metrics) {
    console.warn("calculatePhaseScore: No metrics provided");
    return 0;
  }

  const { cumulativeSway, meanDistFromStart, rangeX, rangeY, elapsedSec } =
    metrics;

  // Edge case: No elapsed time (would cause division by zero)
  if (elapsedSec <= 0) {
    console.warn("calculatePhaseScore: Invalid elapsed time:", elapsedSec);
    return 0;
  }

  // Total range (2-axis: sideways + forward/backward)
  const totalRange = rangeX + rangeY;

  // Composite score (higher = worse balance) - all values in feet
  const compositeScore =
    cumulativeSway / elapsedSec / MCTSIB_CONFIG.VELOCITY_WEIGHT +
    meanDistFromStart / MCTSIB_CONFIG.DISPLACEMENT_WEIGHT +
    totalRange / MCTSIB_CONFIG.RANGE_WEIGHT;

  // Log for calibration data collection (when enabled)
  if (MCTSIB_CONFIG.LOG_CALIBRATION_DATA) {
    console.log("CALIBRATION_DATA:", {
      compositeScore: compositeScore.toFixed(4),
      cumulativeSway: cumulativeSway.toFixed(2),
      meanDistFromStart: meanDistFromStart.toFixed(2),
      totalRange: totalRange.toFixed(2),
      elapsedSec: elapsedSec.toFixed(2),
      swayVelocity: (cumulativeSway / elapsedSec).toFixed(4),
    });
  }

  // Map composite to 0-100 (inverted: lower composite = higher score)
  let phaseScore =
    100 -
    (100 * (compositeScore - MCTSIB_CONFIG.BEST_RAW)) /
      (MCTSIB_CONFIG.WORST_RAW - MCTSIB_CONFIG.BEST_RAW);

  // Time penalty (if patient didn't complete full duration)
  phaseScore = phaseScore * (elapsedSec / MCTSIB_CONFIG.TIME_LIMIT_SEC);

  // Clamp to 0-100
  phaseScore = Math.max(0, Math.min(100, phaseScore));

  return phaseScore;
}

/**
 * Calculates sensory integration scores from the 4 phase scores.
 *
 * Mapping:
 * - Eyes Open, Floor: Baseline (all systems)
 * - Eyes Closed, Floor: Somatosensory + Vestibular → Somatosensory score
 * - Eyes Open, Foam: Visual + Vestibular → Visual score
 * - Eyes Closed, Foam: Vestibular only → Vestibular score
 *
 * @param {Object} phaseScores - { openFloor, closedFloor, openFoam, closedFoam }
 * @returns {Object} - { vestibular, visual, somatosensory, overall }
 */
export function calculateSensoryScores(phaseScores) {
  const { openFloor, closedFloor, openFoam, closedFoam } = phaseScores;

  // Vestibular = eyes closed on foam (pure vestibular - no vision, unstable surface)
  const vestibular = closedFoam;

  // Boost calculation (compensates for weak vestibular systems)
  // Patients with weak vestibular would have artificially low visual/somatosensory
  const baseBoost = (100 - vestibular) / 4;
  const visualBoost = Math.min(baseBoost, 6); // Max 6 points
  const somatosensoryBoost = Math.min(baseBoost, 5); // Max 5 points

  // Visual = eyes open on foam + boost
  const visual = Math.max(0, Math.min(100, openFoam + visualBoost));

  // Somatosensory = eyes closed on floor + boost
  const somatosensory = Math.max(
    0,
    Math.min(100, closedFloor + somatosensoryBoost),
  );

  // Overall = simple average of all 4 conditions
  const overall = (openFloor + closedFloor + openFoam + closedFoam) / 4;

  return {
    vestibular: Math.round(vestibular * 10) / 10, // Round to 1 decimal
    visual: Math.round(visual * 10) / 10,
    somatosensory: Math.round(somatosensory * 10) / 10,
    overall: Math.round(overall * 10) / 10,
  };
}

/**
 * Calculates all scores for a complete mCTSIB assessment.
 * This is the main entry point that orchestrates all calculations.
 *
 * @param {Object} swayData - Object containing sway arrays for all 4 conditions
 * @param {Object} elapsedTimes - Object containing elapsed times for all 4 conditions
 * @returns {Object} - Complete results including metrics, phase scores, and sensory scores
 */
export function calculateAllScores(swayData, elapsedTimes) {
  // Calculate metrics for each sub-assessment
  const metricsOpenFloor = calculateSubAssessmentMetrics(
    swayData.openEyesX,
    swayData.openEyesY,
    elapsedTimes.openEyes,
  );
  const metricsClosedFloor = calculateSubAssessmentMetrics(
    swayData.closedEyesX,
    swayData.closedEyesY,
    elapsedTimes.closedEyes,
  );
  const metricsOpenFoam = calculateSubAssessmentMetrics(
    swayData.openEyesFoamX,
    swayData.openEyesFoamY,
    elapsedTimes.openEyesFoam,
  );
  const metricsClosedFoam = calculateSubAssessmentMetrics(
    swayData.closedEyesFoamX,
    swayData.closedEyesFoamY,
    elapsedTimes.closedEyesFoam,
  );

  // Validate metrics
  validateMetrics(metricsOpenFloor, "open_eyes_floor");
  validateMetrics(metricsClosedFloor, "closed_eyes_floor");
  validateMetrics(metricsOpenFoam, "open_eyes_foam");
  validateMetrics(metricsClosedFoam, "closed_eyes_foam");

  // Calculate phase scores (0-100)
  const openFloorScore = calculatePhaseScore(metricsOpenFloor);
  const closedFloorScore = calculatePhaseScore(metricsClosedFloor);
  const openFoamScore = calculatePhaseScore(metricsOpenFoam);
  const closedFoamScore = calculatePhaseScore(metricsClosedFoam);

  // Calculate sensory integration scores
  const sensoryScores = calculateSensoryScores({
    openFloor: openFloorScore,
    closedFloor: closedFloorScore,
    openFoam: openFoamScore,
    closedFoam: closedFoamScore,
  });

  // Format metrics for response (convert to inches - multiply by 12)
  // Field names kept as _feet for backend compatibility, but values are in INCHES
  const formatMetrics = (m) => {
    if (!m) return null;
    return {
      cumulative_sway_feet: Math.round(m.cumulativeSway * 12 * 1000) / 1000, // Actually inches
      mean_dist_from_start_feet:
        Math.round(m.meanDistFromStart * 12 * 1000) / 1000, // Actually inches
      range_x_feet: Math.round(m.rangeX * 12 * 1000) / 1000, // Actually inches
      range_y_feet: Math.round(m.rangeY * 12 * 1000) / 1000, // Actually inches
      elapsed_sec: Math.round(m.elapsedSec * 100) / 100,
      sway_velocity_feet_per_sec: Math.round(m.swayVelocity * 12 * 1000) / 1000, // Actually inches/sec
    };
  };

  return {
    phase_scores: {
      open_eyes_floor: Math.round(openFloorScore * 10) / 10,
      closed_eyes_floor: Math.round(closedFloorScore * 10) / 10,
      open_eyes_foam: Math.round(openFoamScore * 10) / 10,
      closed_eyes_foam: Math.round(closedFoamScore * 10) / 10,
    },
    sensory_scores: sensoryScores,
    metrics: {
      open_eyes_floor: formatMetrics(metricsOpenFloor),
      closed_eyes_floor: formatMetrics(metricsClosedFloor),
      open_eyes_foam: formatMetrics(metricsOpenFoam),
      closed_eyes_foam: formatMetrics(metricsClosedFoam),
    },
  };
}

/**
 * Calculates overall mCTSIB scores from phase data.
 * Equivalent to the desktop VirtuBalance calculateOverallScores method (CtsibCommon.cs).
 *
 * Accepts the phases array from build_response() and returns sensory scores.
 * Exposed on window so the report renderer can call it directly,
 * matching how the desktop report calls the Kinect DLL method.
 *
 * @param {Array} phases - Array of phase objects with phase_score and mode.code
 * @returns {Object} - { visual, somatosensory, vestibular, overall }
 */
export function calculateOverallScores(phases) {
  const phaseScores = {
    openFloor: 0,
    closedFloor: 0,
    openFoam: 0,
    closedFoam: 0,
  };

  for (const phase of phases) {
    switch (phase.mode.code) {
      case "eyes_open_standing_on_floor":
        phaseScores.openFloor = phase.phase_score;
        break;
      case "eyes_closed_standing_on_floor":
        phaseScores.closedFloor = phase.phase_score;
        break;
      case "eyes_open_standing_on_foam":
        phaseScores.openFoam = phase.phase_score;
        break;
      case "eyes_closed_standing_on_foam":
        phaseScores.closedFoam = phase.phase_score;
        break;
    }
  }

  return {
    scores: calculateSensoryScores(phaseScores),
  };
}

// Expose on window for report renderer to call (replaces desktop Kinect DLL method)
if (typeof window !== "undefined") {
  window.calculateOverallScores = calculateOverallScores;
}

export default {
  calculateSubAssessmentMetrics,
  validateMetrics,
  calculatePhaseScore,
  calculateSensoryScores,
  calculateOverallScores,
  calculateAllScores,
  MCTSIB_CONFIG,
};
